import { ViewContainerRef } from '@angular/core';
import { IWIZError, IWizValidatorDirective } from './../../../interfaces/iwiz-error';
import { WizValidationErrors } from './../../validators/wiz-validators';

export class WizErrorManager {
  private static _instance: WizErrorManager;

  private _validationContexts: Map<any, IWizValidatorDirective>;

  /**
   * Initializes the showing of Errors by default on the application
   */
  public static DEFAULT_SHOW_ERRORS: boolean = false;
  /**
   * Default number of errors to be shown on validation. Defaults to 1
   */
  public static DEFAULT_NUM_ERRORS: number = 1;

  constructor() {
    if (!!WizErrorManager._instance) {
      throw new Error('SINGLETON ERROR. This class is a singleton. You must not create another instance');
    }

    this._validationContexts = new Map<any, IWizValidatorDirective>();
  }

  public static get instance(): WizErrorManager {
    if (!WizErrorManager._instance) {
      WizErrorManager._instance = new WizErrorManager();
    }

    return WizErrorManager._instance;
  }

  /**
   * Registers a validation context for a component
   *
   * @param pComponent The component for which to register the validation context
   * @param pContext Validation context for the component
   */
  public registerValidationContext(pComponent: any, pContext: IWizValidatorDirective): void {
      this._validationContexts.set(pComponent, pContext);
  }

  /**
   * Deletes the component validation context from the registration map
   *
   * @param pComponent The component that's being destroyed
   */
  public unregisterValidationContext(pComponent: any): void {
      this._validationContexts.delete(pComponent);
  }

  /**
   * Indicates if a container is valid
   *
   * @param pShowErrors Indicates if must be shown in the container
   * @param pErrorContainer Shows all the errors in the container given rather than the component error container
   * @param pNumResults Number of results to show in the container. Default -1 to show all results
   * @param pClear Indicates if we must clear the container before showing results
   */
  public isContainerValid(pContainer: any,
                          pShowErrors: boolean = WizErrorManager.DEFAULT_SHOW_ERRORS,
                          pDeep: boolean = false,
                          pErrorContainer: ViewContainerRef = null,
                          pNumResults: number = WizErrorManager.DEFAULT_NUM_ERRORS,
                          pClear: boolean = true): boolean {
    const errors: WizValidationErrors = this.getErrors(pContainer, pShowErrors, pDeep, pErrorContainer, pNumResults, pClear);
    return !errors || errors.length === 0;
  }

  /**
   * Indicates if a unique Element is valid
   *
   * @param pShowErrors Indicates if must be shown in the container
   * @param pErrorContainer Shows all the errors in the container given rather than the component error container
   * @param pNumResults Number of results to show in the container. Default -1 to show all results
   * @param pClear Indicates if we must clear the container before showing results
   */
  public isElementValid(pElement: any,
                        pShowErrors?: boolean,
                        pErrorContainer: ViewContainerRef = null,
                        pNumResults: number = WizErrorManager.DEFAULT_NUM_ERRORS,
                        pClear: boolean = true): boolean {
    const errors: WizValidationErrors = this.getErrors(pElement, pShowErrors, false, pErrorContainer, pNumResults, pClear);
    return !errors || errors.length === 0;
  }

  /**
   * Validates a container or an element.
   *
   * If we encounter any errors, we return them
   *
   * @param pShowErrors Indicates if must be shown in the container
   * @param pDeep Deep checks. Container will check sub containers recursively if they are any
   * @param pErrorContainer Shows all the errors in the container given rather than the component error container
   * @param pNumResults Number of results to show in the container. Default -1 to show all results
   * @param pClear Indicates if we must clear the container before showing results
   */
  public getErrors( pElement: IWIZError,
                    pShowErrors?: boolean,
                    pDeep: boolean = false,
                    pErrorContainer: ViewContainerRef = null,
                    pNumErrors: number = -1,
                    pClear: boolean = true): WizValidationErrors {
    const validationContext: IWizValidatorDirective = this._validationContexts.get(pElement);

    let errors: WizValidationErrors = [];

    // contrat d'interface IWizError: finalisation de l'état du composant avant soumission pour check
    //par exemple, pour le dateField, complétion de la saisie pour en faire une date, et alimentation du wizValue
    pElement.prepareForCheck();
    //console.log('errorMgr',pElement.getWizValue(),pElement.getLastGoodValue());
    if (validationContext) {
      errors = validationContext.check(pShowErrors, pDeep, pErrorContainer, pNumErrors, pClear);
    }

    if (!errors || errors.length === 0) { // alimentation du goodValue si pas d'erreur
      pElement.setLastGoodValue(pElement.getWizValue());
      //console.log('em setlasGood',pElement.getLastGoodValue());
    }


    return errors;
  }

  /**
   * rollback sur la dernière valeur correcte du composant
   * Déclenché à la demande / sur focusOut en mode Rollback (obj IWizFocusable)
   */
  public rollback(pElement: any) {

    let elementAsIWizError:IWIZError = pElement as IWIZError;
    if(elementAsIWizError) {
      // console.log('em rollback',pElement.getLastGoodValue());
      pElement.setWizValue(pElement.getLastGoodValue());
      pElement.wizRollback();// méthode complémentaires du compo
  }
  }
}
